/**
 * comprobante-list.ts — APR-9 (openspec/changes/cuentas-pagar-historial-y-gestion-pagos).
 * Response contract for `GET /sumi/cuentas-pagar/comprobantes`. `estado` is a
 * closed vocabulary computed server-side (`ListComprobantesUseCase`) — the
 * frontend only renders it (Badge mapping), never recomputes it.
 *
 * Spec: openspec/changes/cuentas-pagar-historial-y-gestion-pagos/specs/cuentas-pagar-http-routes/spec.md APR-9
 * Design: openspec/changes/cuentas-pagar-historial-y-gestion-pagos/design.md "Interfaces / Contracts"
 */
import { z } from 'zod';
import { CurrencyCodeSchema } from '@suite/contracts-kernel';
import { ComprobanteTipoSchema, GananciasConceptoSchema } from './vocab.js';

export const COMPROBANTE_ESTADOS = ['pendiente', 'pagado_parcial', 'pagado_total', 'vencido', 'anulado'] as const;
export const ComprobanteEstadoSchema = z.enum(COMPROBANTE_ESTADOS);
export type ComprobanteEstado = z.infer<typeof ComprobanteEstadoSchema>;

export const ComprobanteListRowSchema = z.object({
  id: z.string().uuid(),
  supplierId: z.string().uuid(),
  tipo: ComprobanteTipoSchema,
  numero: z.string().nullable(),
  /** `FAC A 0001-00000012` — the same label the payment screens use. */
  comprobanteLabel: z.string(),
  moneda: CurrencyCodeSchema,
  montoTotal: z.string(),
  pendiente: z.string(),
  fechaVencimiento: z.string().nullable(),
  estado: ComprobanteEstadoSchema,
  /** ap-anular-editar-factura — live and untouched by any payment/NC: the row offers Editar and Anular. */
  modificable: z.boolean(),
  /** Migration 0043 — the supplier has a "libre de deuda" not expired. Default false: the SPA deploys before the backend. */
  libreDeuda: z.boolean().default(false),
  /** Migration 0044 — the invoice's Ganancias concept and its emission date: the alta pre-selects the supplier's last one. */
  gananciasConcepto: GananciasConceptoSchema.nullable().default(null),
  fechaEmision: z.string().nullable().default(null),
});
export type ComprobanteListRow = z.infer<typeof ComprobanteListRowSchema>;

export const ComprobanteListResponseSchema = z.object({
  items: z.array(ComprobanteListRowSchema),
});
export type ComprobanteListResponse = z.infer<typeof ComprobanteListResponseSchema>;

/** Query param validation for `GET /sumi/cuentas-pagar/comprobantes?estado=&supplierId=`. */
export const ComprobanteListQuerySchema = z.object({
  estado: ComprobanteEstadoSchema.optional(),
  supplierId: z.string().uuid().optional(),
});
export type ComprobanteListQuery = z.infer<typeof ComprobanteListQuerySchema>;
